import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Truck } from "lucide-react";
import { LINKS, IMAGES, INFO } from "../../data";

const lines = ["67", "WINGS", "& TENDERS"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-black">
      {/* Parallax background image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroWings}
          alt="Saucy glazed 67 wings with sesame"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 flex min-h-[100svh] flex-col justify-end px-5 sm:px-8 lg:px-12 pb-14 pt-28 mx-auto max-w-[1600px]"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="font-body text-xs sm:text-sm uppercase tracking-[0.35em] text-neon-green mb-4"
        >
          Salinas, CA · Fresh Never Frozen · Sabor Con Sazón
        </motion.p>

        <h1 className="font-heading uppercase leading-[0.82] tracking-tighter">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block text-7xl sm:text-8xl lg:text-[10rem] xl:text-[13rem] ${
                  i === 1 ? "text-neon-yellow neon-glow-yellow" : "text-neon-green neon-glow-green"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
        >
          <Link
            to="/order"
            data-testid="hero-order-btn"
            className="group inline-flex items-center justify-center gap-2 font-heading text-2xl tracking-wide bg-neon-green text-black px-8 py-4 hover:bg-neon-yellow transition-colors duration-200 active:scale-[0.97]"
          >
            Order Online / Pickup
            <ArrowRight size={22} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a
            href={LINKS.doordash}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-delivery-btn"
            className="inline-flex items-center justify-center gap-2 font-heading text-xl tracking-wide border border-neon-yellow text-neon-yellow px-6 py-4 hover:bg-neon-yellow hover:text-black transition-colors duration-200"
          >
            <Truck size={20} /> Delivery
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-5"
        >
          <div className="flex items-center gap-2 text-neutral-300">
            <div className="flex text-neon-yellow">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <span className="font-body text-sm">Loved in {INFO.address.split(",")[1]?.trim() || "Salinas"}</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
